import { useContext, useMemo } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';
import { usePolicyGroupId } from 'hooks/usePolicyGroupId';
import { useGroupedBillingSync } from 'hooks/useGroupedBillingSync';

import { Flex } from '@chakra-ui/react';

import SharedSubmit from 'components/shared/Submit';
import {
  StepOne,
  StepTwo,
  StepThree,
  GroupedStepOne,
  GroupedStepTwo,
  GroupedStepThree,
} from 'components/shared/steps';

import { apiCreateApplicationLog } from 'api';

import { AuthContext } from 'contexts/AuthContext';

import useIsBinderDisabled from 'views/home_form/hooks/useIsBinderDisabled';

import {
  changeIsDataSaved,
  setBinderPDF,
} from 'reducers/homePersonalFormReducer';

const Submit = () => {
  const { user } = useContext(AuthContext);
  const dispatch = useAppDispatch();

  const uuid = useAppSelector((state) => state.homePersonalForm.uuid);
  const policyGroupId = usePolicyGroupId(uuid);

  const { isInMultiBinderGroup } = useGroupedBillingSync(uuid, {
    policyGroupId,
  });

  const data = useAppSelector((state) => state.homePersonalForm.data);
  const remarks = useAppSelector((state) => state.homePersonalForm.remarks);
  const files = useAppSelector((state) => state.homePersonalForm.files);
  const payments = useAppSelector((state) => state.homePersonalForm.payments);
  const isDataSaved = useAppSelector(
    (state) => state.homePersonalForm.isDataSaved,
  );
  const missingMandatoryFields = useAppSelector(
    (state) => state.homePersonalForm.missingMandatoryFields,
  );
  const hasSpecialCircumstances = useAppSelector(
    (state) =>
      state.homePersonalForm.data.broker_questionnaire
        .has_special_circumstances,
  );

  const isBinderDisabled = useIsBinderDisabled();

  const hasMissingPaymentDetails = useMemo(() => {
    const selectedPaymentMethod = data.billing?.payment_method;

    // Only check if payment method is EFT or credit card
    if (
      selectedPaymentMethod === 'eft' ||
      selectedPaymentMethod === 'credit_card'
    ) {
      if (!payments || payments.length === 0) return true;

      // Check if there's a payment entry for the selected method in payment_method section
      const hasPaymentEntry = payments.some(
        (payment) =>
          payment.type_name === selectedPaymentMethod &&
          payment.section_name === 'payment_method',
      );

      return !hasPaymentEntry;
    }

    return false;
  }, [data.billing?.payment_method, payments]);

  const hasMissingDownPaymentDetails = useMemo(() => {
    const hasDownPayment = data.billing?.has_down_payment;
    const downPaymentMethod = data.billing?.down_payment_method;

    // Check down payment method if down payment is selected and method is credit card
    if (hasDownPayment && downPaymentMethod === 'credit_card') {
      if (!payments || payments.length === 0) return true;

      // Check if there's a payment entry for down payment
      const hasDownPaymentEntry = payments.some(
        (payment) =>
          payment.type_name === 'credit_card' &&
          payment.section_name === 'down_payment',
      );

      return !hasDownPaymentEntry;
    }

    return false;
  }, [
    data.billing?.has_down_payment,
    data.billing?.down_payment_method,
    payments,
  ]);

  const hasMissingPriorPolicyInfo = useMemo(() => {
    const isFirstTimeInsured = data.policy_history.is_first_time_insured;
    const policies = data.policy_history.policies;

    if (isFirstTimeInsured === false && policies.length === 0) {
      return true;
    }

    return false;
  }, [
    data.policy_history?.is_first_time_insured,
    data.policy_history?.policies,
  ]);

  const hasHeatingNotProfessionallyInstalled = useMemo(() => {
    if (!data.dwellings || data.dwellings.length === 0) return false;

    return data.dwellings.some((dwelling) => {
      const ratingInfo = dwelling.rating_information;
      if (!ratingInfo) return false;

      const primaryNotInstalled =
        ratingInfo.primary_heating_type?.is_professionally_installed === false;
      const auxiliaryNotInstalled =
        ratingInfo.auxiliary_heating_type?.is_professionally_installed ===
        false;

      return primaryNotInstalled || auxiliaryNotInstalled;
    });
  }, [data.dwellings]);

  const hasNoneLegalApplicantInterest = useMemo(() => {
    return data.applicants.some((applicant) => applicant.interest === 'None');
  }, [data.applicants]);

  const renderFinalSteps = () => {
    if (isInMultiBinderGroup) {
      return (
        <Flex justify="space-between" align="center" gap="20px">
          <GroupedStepOne formStateKey="homePersonalForm" />
          <GroupedStepTwo formStateKey="homePersonalForm" />
          <GroupedStepThree formStateKey="homePersonalForm" />
        </Flex>
      );
    }

    return (
      <Flex justify="space-between" align="center" gap="20px">
        <StepOne formStateKey="homePersonalForm" />
        <StepTwo formStateKey="homePersonalForm" />
        <StepThree formStateKey="homePersonalForm" />
      </Flex>
    );
  };

  const handleSaveSuccess = () => {
    dispatch(setBinderPDF(null));
  };

  return (
    <SharedSubmit
      formStateKey="homePersonalForm"
      data={data}
      remarks={remarks}
      files={files}
      missingMandatoryFields={missingMandatoryFields}
      isDataSaved={isDataSaved}
      isFormDisabled={isBinderDisabled}
      user={user}
      changeIsDataSaved={(value) => dispatch(changeIsDataSaved(value))}
      saveApiCall={(uuid, data, remarks, files, isFormComplete) => {
        return apiCreateApplicationLog(
          uuid,
          data,
          remarks,
          files,
          isFormComplete,
        ).then((result) => {
          handleSaveSuccess();
          return result;
        });
      }}
      renderFinalSteps={renderFinalSteps}
      hasMissingPaymentDetails={hasMissingPaymentDetails}
      hasMissingDownPaymentDetails={hasMissingDownPaymentDetails}
      hasMissingPriorPolicyInfo={hasMissingPriorPolicyInfo}
      hasSpecialCircumstances={hasSpecialCircumstances}
      hasHeatingNotProfessionallyInstalled={
        hasHeatingNotProfessionallyInstalled
      }
      hasNoneLegalApplicantInterest={hasNoneLegalApplicantInterest}
    />
  );
};

export default Submit;
